import { BLOCKED_PANEL_COUNTRIES_ISO3 } from "./countries/blockedPanelCountries";
import { FilterId, PanelType } from "./types";
import { getCountryDisplayName } from "@/components/outreach/recruitment/panelPrediction/panelRegistry/countries";

// We trust recruited_responses attribution for studies whose first response is on or after this date.
// Earlier studies may have missing records from the legacy Prolific approval path removed in
// https://github.com/MerlinAGI/survey-ai/pull/2638; this cutoff is the day after that PR merged.
export const PANEL_ATTRIBUTION_START = "2026-03-27T00:00:00.000Z";

/** maximum number of available places for a single study */
export const MAX_TOTAL_AVAILABLE_PLACES = 2000;
/** maximum completion time for a study in minutes */
export const MAX_COMPLETION_TIME = 120;

export const age_filter_id = "age" as FilterId;
export const LLM_COUNTRY_FILTER_ID = "country";
export const LLM_AGE_FILTER_ID = "age";
export const LLM_OCCUPATION_FILTER_ID = "occupation";
export const approval_rate_id = "approval_rate" as FilterId;
export const country_filter_id = "current-country-of-residence" as FilterId;
export const gender_filter_id = "gender" as FilterId;

export const RECRUITMENT_PANELS: PanelType[] = [
  "prolific",
  "user_interviews",
  "respondent_b2c",
  "respondent_b2b",
];

export const DEFAULT_STUDY_DESCRIPTION =
  "Join an innovative AI-powered interview study by Listen Labs. Our AI interviewer will ask you questions about your experiences and opinions, adapting based on your responses. Your insights will help shape future products and services. The process is designed to be engaging, efficient, and respectful of your time. Participate in cutting-edge research from the comfort of your own device!";
export const DEFAULT_SCREENER_DESCRIPTION = (need_camera: boolean) =>
  `\n\nThere is a screener, if you do not qualify for the study, you will still receive a small incentive. Please ensure that you are in a quiet environment with access to high-quality ${need_camera ? "video and audio" : "audio"}; otherwise, you might get rejected.${need_camera ? "\n\nEnsure you are wearing appropriate clothing (T-shirt, etc.) otherwise you could be rejected and receive no incentive." : ""}`;

export const UPDATE_TIME_ESTIMATE_RESPONSE_THRESHOLD = 20;
export const QUALIFICATION_RATE_PAUSE_THRESHOLD = 0.1;

export const MAX_MINI_INTERVIEW_MINUTES = 10;
export const MAX_SHORT_INTERVIEW_MINUTES = 20;

export const DEFAULT_PROLIFIC_MAX_PARTICIPANTS = 200_000;
// Respondent markets a "3M+ vetted participants" panel; used as the static
// pool-size denominator since their API has no unfiltered total-count call.
export const DEFAULT_RESPONDENT_MAX_PARTICIPANTS = 3_000_000;
export const UI_PARTICIPANT_COUNT_MULTIPLIER = 25;
// Technically this is 5,000 per their docs but we multiply by 25
export const DEFAULT_UI_MAX_PARTICIPANTS =
  5_000 * UI_PARTICIPANT_COUNT_MULTIPLIER;

type CountrySetting = {
  letters?: string;
  digits?: string;
  userInterviewsName?: string;
  prolificName?: string;
};

export const COUNTRY_NAME_TO_CODES: Record<string, CountrySetting> = {
  Argentina: { letters: "AR" },
  Australia: { letters: "AU", digits: "19" },
  Austria: { letters: "AT", digits: "20" },
  Belgium: { letters: "BE", digits: "27" },
  Brazil: { letters: "BR", digits: "37" },
  Bulgaria: { letters: "BG" },
  Canada: { letters: "CA", digits: "45" },
  Chile: { letters: "CL", digits: "50" },
  Croatia: { digits: "61" },
  Czechia: { letters: "CZ", digits: "65", prolificName: "Czech Republic" },
  Denmark: { letters: "DK", digits: "66" },
  Estonia: { digits: "75" },
  Finland: { letters: "FI", digits: "80" },
  France: { letters: "FR", digits: "4" },
  Germany: { letters: "DE", digits: "3" },
  Greece: { letters: "GR", digits: "89" },
  Hungary: { digits: "104" },
  Iceland: { digits: "105" },
  India: { letters: "IN" },
  Ireland: { letters: "IE", digits: "2" },
  Israel: { digits: "111" },
  Italy: { letters: "IT", digits: "112" },
  Japan: { letters: "JP", digits: "114" },
  Korea: { digits: "120" },
  Latvia: { digits: "124" },
  Luxembourg: { digits: "131" },
  Malaysia: { letters: "MY" },
  Mexico: { letters: "MX", digits: "145" },
  Netherlands: { letters: "NL", digits: "158" },
  "New Zealand": { letters: "NZ", digits: "160" },
  Norway: { letters: "NO", digits: "167" },
  Poland: { letters: "PL", digits: "178" },
  Portugal: { letters: "PT", digits: "179" },
  "Puerto Rico": { letters: "PR" },
  Romania: { letters: "RO" },
  Singapore: { letters: "SG" },
  Slovakia: { digits: "203" },
  Slovenia: { digits: "204" },
  "South Africa": { letters: "ZA", digits: "207" },
  Spain: { letters: "ES", digits: "5" },
  Sweden: { letters: "SE", digits: "215" },
  Switzerland: { letters: "CH", digits: "216" },
  "United Kingdom": { letters: "GB", digits: "0" },
  "United States": { letters: "US", digits: "1" },
};

const BLOCKED_PANEL_COUNTRY_NAMES = BLOCKED_PANEL_COUNTRIES_ISO3.map(
  getCountryDisplayName
);

export const SUPPORTED_COUNTRY_NAME_TO_CODES: Record<string, CountrySetting> =
  Object.fromEntries(
    Object.entries(COUNTRY_NAME_TO_CODES).filter(
      ([name, _codes]) => !BLOCKED_PANEL_COUNTRY_NAMES.includes(name)
    )
  );

export const NOT_IN_USER_INTERVIEWS_COUNTRIES = Object.keys(
  SUPPORTED_COUNTRY_NAME_TO_CODES
).filter(
  (country) => SUPPORTED_COUNTRY_NAME_TO_CODES[country].letters === undefined
);
export const NOT_IN_PROLIFIC_COUNTRIES = Object.keys(
  SUPPORTED_COUNTRY_NAME_TO_CODES
).filter(
  (country) => SUPPORTED_COUNTRY_NAME_TO_CODES[country].digits === undefined
);

export const RECRUIT_TOP_UP_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
export const RECRUIT_TOP_UP_UNHIDE_GRACE_MS = 3 * 60 * 60 * 1000;
export const RECRUIT_TOP_UP_SETTLE_AFTER_MS = 30 * 60 * 1000;
